import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import { useMap } from "react-leaflet";
import { useTheme } from "@mui/material";
import "leaflet-draw";
import "leaflet-draw/dist/leaflet.draw.css";

// Camada que renderiza um elemento do tipo 4 (área/polígono).

// Dois panes: na visualização a área fica ABAIXO do overlay Pixi (overlayPane,
// zIndex 400) para não cobrir os ícones; ao editar, sobe ACIMA do Pixi para
// permitir interagir com os vértices e a borda.
const POLYGON_PANE = "easymaps-polygon-pane";
const POLYGON_PANE_ZINDEX = 300;
const POLYGON_PANE_EDIT = "easymaps-polygon-pane-edit";
const POLYGON_PANE_EDIT_ZINDEX = 400; //
const STROKE_WEIGHT = 4;

function ensurePane(map, name, zIndex) {
  if (!map) return;
  if (!map.getPane(name)) {
    map.createPane(name);
  }
  const pane = map.getPane(name);
  if (pane && pane.style.zIndex !== String(zIndex)) {
    pane.style.zIndex = String(zIndex);
  }
}

function ensurePolygonStyles() {
  if (typeof document === "undefined") return;
  if (document.getElementById("easymaps-polygon-style")) return;
  const style = document.createElement("style");
  style.id = "easymaps-polygon-style";
  style.textContent = `
    /* Apenas a borda captura cliques; o interior (alpha) deixa o clique
       passar para os ícones do Pixi que ficam abaixo. Especificidade maior
       que a regra padrão do Leaflet (.leaflet-pane > svg path.leaflet-interactive). */
    .leaflet-pane > svg path.easymaps-polygon-path.leaflet-interactive,
    .leaflet-pane > svg path.easymaps-polygon-hit.leaflet-interactive {
      pointer-events: stroke;
    }
    .leaflet-editing-icon {
      border-radius: 50% !important;
      width: 12px !important;
      height: 12px !important;
      margin-left: -6px !important;
      margin-top: -6px !important;
      background: #3388ff !important;
      border: 2px solid #ffffff !important;
      box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.55), 0 1px 4px rgba(0, 0, 0, 0.55) !important;
      box-sizing: border-box !important;
    }
  `;
  document.head.appendChild(style);
}

function toLatLngs(points) {
  return (points || [])
    .map((p) => [parseFloat(p?.lat), parseFloat(p?.long)])
    .filter(([la, ln]) => Number.isFinite(la) && Number.isFinite(ln));
}

function layerToPoints(latlngs) {
  const flat = Array.isArray(latlngs?.[0]) ? latlngs[0] : latlngs;
  return (flat || []).map((ll) => ({ lat: ll.lat, long: ll.lng }));
}

// A cor pode vir como hex de 8 dígitos (#rrggbbaa). O SVG/Leaflet lida melhor
// com a cor (6 dígitos) e a opacidade separadas, então extraímos cada parte.
function hex6(h) {
  return typeof h === "string" && h.length === 9 ? h.slice(0, 7) : h;
}

function hexAlphaToOpacity(h, fallback) {
  if (typeof h === "string" && h.length === 9) {
    const a = parseInt(h.slice(7), 16);
    return Number.isFinite(a) ? a / 255 : fallback;
  }
  return fallback;
}

export default function PolygonLayer({
  element,
  editable,
  selected,
  onNodeClick = () => {},
  handleUpdatePolygonPoints = () => {},
}) {
  const theme = useTheme();
  const map = useMap();
  const layerRef = useRef(null);
  const hitRef = useRef(null);

  ensurePane(map, POLYGON_PANE, POLYGON_PANE_ZINDEX);
  ensurePane(map, POLYGON_PANE_EDIT, POLYGON_PANE_EDIT_ZINDEX);
  ensurePolygonStyles();

  const polygon = element?.elementConfig?.config?.polygon || {};
  const borderColor = polygon.color || theme.palette.secondary.main;
  // Preenchimento: usa a cor de preenchimento própria (com sua opacidade via
  // alpha); se não houver, cai na cor da borda renderizada a 20% de opacidade.
  // O react-colorful só inclui o byte de alpha quando a opacidade é < 1, então
  // uma cor de preenchimento sem alpha (#rrggbb) é opaca (opacidade 1).
  const fillRaw = polygon.fillColor || "";
  const fillColor = fillRaw ? hex6(fillRaw) : hex6(borderColor);
  const fillOpacity = fillRaw ? hexAlphaToOpacity(fillRaw, 1) : 0.2;
  const latlngs = toLatLngs(polygon.points);
  const hasPoints = latlngs.length >= 3;
  const isEditing = !!(editable && selected);
  const elementKey = element?.id ?? element?.tempId;

  // Ao entrar em modo de edição (editable), TODAS as áreas sobem para o pane 500
  // para ficarem acima do Pixi e poderem ser clicadas/selecionadas.
  const paneName = editable ? POLYGON_PANE_EDIT : POLYGON_PANE;
  const pointsKey = latlngs.map((ll) => ll.join()).join(";");
  const geometryKey = isEditing ? "editing" : pointsKey;

  // "draw" = elemento novo ainda sem geometria (desenho em andamento);
  // "shape" = polígono já com pontos (visualização ou edição de vértices).
  const [mode, setMode] = useState(isEditing && !hasPoints ? "draw" : "shape");

  const borderColorRef = useRef(borderColor);
  borderColorRef.current = borderColor;
  const fillColorRef = useRef(fillColor);
  fillColorRef.current = fillColor;
  const fillOpacityRef = useRef(fillOpacity);
  fillOpacityRef.current = fillOpacity;
  const elementRef = useRef(element);
  elementRef.current = element;
  const onNodeClickRef = useRef(onNodeClick);
  onNodeClickRef.current = onNodeClick;
  const handleUpdateRef = useRef(handleUpdatePolygonPoints);
  handleUpdateRef.current = handleUpdatePolygonPoints;

  useEffect(() => {
    setMode(isEditing && !hasPoints ? "draw" : "shape");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elementKey]);

  // MODO DESENHO (elemento novo)
  useEffect(() => {
    if (!map || mode !== "draw") return;

    const drawer = new L.Draw.Polygon(map, {
      allowIntersection: true,
      showArea: false,
      shapeOptions: {
        pane: paneName,
        className: "easymaps-polygon-path",
        color: borderColorRef.current,
        weight: STROKE_WEIGHT,
        fillColor: fillColorRef.current,
        fillOpacity: fillOpacityRef.current,
      },
    });
    drawer.enable();

    // Sincroniza os vértices já confirmados em tempo real, para que o botão
    // "Adicionar" libere a partir de 3 pontos e salvar finalize a área —
    // sem exigir o clique no ponto inicial para fechar.
    const syncVertices = () => {
      const pts = (drawer._markers || []).map((m) => ({
        lat: m.getLatLng().lat,
        long: m.getLatLng().lng,
      }));
      handleUpdateRef.current(elementRef.current, pts);
    };
    const onCreated = (e) => {
      handleUpdateRef.current(
        elementRef.current,
        layerToPoints(e.layer.getLatLngs()),
      );
      setMode("shape");
    };
    map.on(L.Draw.Event.DRAWVERTEX, syncVertices);
    map.on(L.Draw.Event.CREATED, onCreated);

    return () => {
      map.off(L.Draw.Event.DRAWVERTEX, syncVertices);
      map.off(L.Draw.Event.CREATED, onCreated);
      try {
        drawer.disable();
      } catch (_) {}
    };
  }, [map, mode, elementKey, paneName]);

  // MODO FORMA (polígono persistido: visualização ou edição de vértices)
  useEffect(() => {
    if (!map || mode !== "shape" || !hasPoints) return;

    const pts = toLatLngs(
      elementRef.current?.elementConfig?.config?.polygon?.points,
    );

    const layer = L.polygon(pts, {
      pane: paneName,
      className: "easymaps-polygon-path",
      color: borderColorRef.current,
      weight: STROKE_WEIGHT,
      fillColor: fillColorRef.current,
      fillOpacity: fillOpacityRef.current,
      interactive: false,
    });
    layer.addTo(map);
    layerRef.current = layer;

    const hit = L.polygon(pts, {
      pane: paneName,
      className: "easymaps-polygon-hit",
      color: "#000000",
      weight: 16,
      opacity: 0,
      fill: false,
      interactive: true,
    });
    hit.addTo(map);
    hitRef.current = hit;
    hit.on("click", (e) => {
      L.DomEvent.stopPropagation(e);
      onNodeClickRef.current(e, elementRef.current);
    });

    if (isEditing && layer.editing) {
      try {
        layer.editing.enable();
      } catch (_) {}
      const pushEdit = () => {
        handleUpdateRef.current(
          elementRef.current,
          layerToPoints(layer.getLatLngs()),
        );
      };
      layer.on("edit", pushEdit);
    }

    return () => {
      try {
        if (layer.editing) layer.editing.disable();
      } catch (_) {}
      layer.off();
      hit.off();
      map.removeLayer(layer);
      map.removeLayer(hit);
      layerRef.current = null;
      hitRef.current = null;
    };
  }, [map, mode, hasPoints, isEditing, elementKey, geometryKey, paneName]);

  // Atualiza as cores sem recriar a camada
  useEffect(() => {
    if (layerRef.current) {
      layerRef.current.setStyle({
        color: borderColor,
        fillColor,
        fillOpacity,
      });
    }
  }, [borderColor, fillColor, fillOpacity]);

  return null;
}
